import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { ClientConfig } from '@core/models/client.model';

import { CLIENT_PERSISTENCE } from '@core/state/persistence';

export interface PersistedClientContext {
  slug: string;

  config: ClientConfig;

  validatedAt: number;
}

const STORAGE_KEY = 'd2d_client_context';

const TTL_MS = 3 * 60 * 60 * 1000; // 3 horas

@Injectable({ providedIn: 'root' })
export class ClientContextPersistenceService {
  private readonly storage = inject(CLIENT_PERSISTENCE);
  private readonly document = inject(DOCUMENT);

  get(): PersistedClientContext | null {
    if (this.shouldInvalidate()) {
      this.clear();

      return null;
    }

    const entry = this.storage.get<PersistedClientContext>(STORAGE_KEY);

    if (!entry) return null;

    if (Date.now() - entry.validatedAt > TTL_MS) {
      this.clear();

      return null;
    }

    return entry;
  }

  set(slug: string, config: ClientConfig): void {
    this.storage.set<PersistedClientContext>(STORAGE_KEY, {
      slug,

      config,

      validatedAt: Date.now(),
    });
  }

  clear(): void {
    this.storage.remove(STORAGE_KEY);
  }

  private shouldInvalidate(): boolean {
    const search = this.document.defaultView?.location.search ?? '';
    const params = new URLSearchParams(search);

    return params.get('web_config_context') === 'false';
  }
}
